import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnRedo,
  BtnStrikeThrough,
  BtnUndo,
  createButton,
  Separator,
  Toolbar,
  useEditorState,
} from 'react-simple-wysiwyg';

import { Theme } from '@app/theme';
import { Button } from '@app/core';
import { QuoteBlock, QuoteTextInfo } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginBottom: theme.marginBase * 2,
  },
  hint: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    flex: 1,
  },
  editor: {
    '& [data-var]': {
      backgroundColor: theme.colors.grayBackground,
      border: `1px solid ${theme.colors.grayBorder}`,
      borderRadius: theme.borderRadius.std,
      color: theme.colors.inkSecondary,
      display: 'inline-block',
      padding: [theme.marginBase / 2, theme.marginBase],
      margin: theme.marginBase / 4,
      maxWidth: '100%',
      userSelect: 'none',
    },
    '& [data-columns]': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: theme.marginBase * 2,
      border: `1px dashed ${theme.colors.grayBorder}`,
      padding: theme.marginBase,
      position: 'relative',
      marginBottom: theme.marginBase * 2,
    },
    '& [data-columns]::after': {
      content: 'var(--quote-full-width-label, "")',
      position: 'absolute',
      left: '50%',
      bottom: 0,
      transform: 'translate(-50%, 50%)',
      padding: [0, theme.marginBase],
      backgroundColor: theme.colors.white,
      ...theme.fonts.caption,
      fontSize: 11,
      lineHeight: 1,
      color: theme.colors.inkSecondary,
      whiteSpace: 'nowrap',
      userSelect: 'none',
      pointerEvents: 'none',
    },
    '& [data-column] + [data-column]': {
      borderLeft: `1px dashed ${theme.colors.grayBorder}`,
      paddingLeft: theme.marginBase * 2,
    },
    '& [data-column]:last-child': {
      textAlign: 'right',
    },
    '& [data-block]': {
      display: 'inline-block',
      textAlign: 'left',
    },
    '& [data-column]:last-child > p, & [data-column]:last-child > ul': {
      textAlign: 'left',
    },
    '& p[data-align="justify"], & div[data-align="justify"], & li[data-align="justify"]':
      {
        textAlign: 'justify',
      },
    '& li': {
      marginLeft: 40,
    },

    '& .rsw-ce': {
      minHeight: theme.marginBase * 28,
      ...theme.fonts.caption,
      color: theme.colors.inkPrimary,
    },
    '& .rsw-editor': {
      borderColor: theme.colors.grayBorder,
      borderRadius: theme.borderRadius.std,
    },
  },
  missing: {
    ...theme.fonts.caption,
    color: theme.colors.redAccessible,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginTop: theme.marginBase,
  },
}));

const BLOCK_TAGS = /^(P|DIV|LI)$/;

const blockOf = (root: HTMLElement, node: Node | null): HTMLElement | null => {
  if (!node || !root.contains(node)) {
    return null;
  }

  let current: Node | null = node;

  while (current && current !== root) {
    if (current instanceof HTMLElement && BLOCK_TAGS.test(current.tagName)) {
      return current;
    }
    current = current.parentNode;
  }

  return null;
};

const toggleJustify = ({ $el }: { $el?: HTMLElement | null }): void => {
  const selection = document.getSelection();
  const block = $el && blockOf($el, selection?.anchorNode ?? null);

  if (!block) {
    return;
  }

  if (block.getAttribute('data-align') === 'justify') {
    block.removeAttribute('data-align');
  } else {
    block.setAttribute('data-align', 'justify');
  }

  $el.dispatchEvent(new Event('input', { bubbles: true }));
};

const JustifyButton = createButton(
  'Justify',
  <i className="ri-align-justify" />,
  toggleJustify,
);

const BtnJustify = () => {
  const { $el, $selection } = useEditorState();
  const active =
    !!$el &&
    $el.contains(document.activeElement) &&
    blockOf(
      $el,
      document.getSelection()?.anchorNode ?? $selection ?? null,
    )?.getAttribute('data-align') === 'justify';

  return <JustifyButton data-active={active} />;
};

type Blocks = QuoteTextInfo['blocks'];

const parseBody = (html: string): HTMLElement =>
  new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;

const isBlank = (node: Node): boolean =>
  node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();

const isEmpty = (node: Element): boolean =>
  Array.from(node.childNodes).every(
    (child) => isBlank(child) || child.nodeName === 'BR',
  );

const PARAGRAPH_TAGS = /^(P|DIV|BLOCKQUOTE|H[1-6]|TABLE)$/;

const LIST_BLOCKS = [
  'p',
  'div',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
]
  .map((tag) => `li > ${tag}`)
  .join(', ');

const liftListBlock = (block: Element): Node | null => {
  const item = block.parentElement as HTMLElement;
  const list = item.parentElement as HTMLElement;
  const children = Array.from(item.childNodes);
  let from = children.indexOf(block);

  for (let i = from - 1; i >= 0; i--) {
    if (children[i].nodeName === 'BR') {
      children[i].remove();
      from = i + 1;
      break;
    }
  }

  const fragment = document.createDocumentFragment();
  let inline: HTMLParagraphElement | null = null;

  children.slice(from).forEach((child) => {
    if (PARAGRAPH_TAGS.test(child.nodeName)) {
      inline = null;
      fragment.appendChild(child);
    } else if (!isBlank(child) || inline) {
      if (!inline) {
        inline = document.createElement('p');
        fragment.appendChild(inline);
      }
      inline.appendChild(child);
    }
  });

  const following: Node[] = [];
  for (let next = item.nextSibling; next; next = next.nextSibling) {
    following.push(next);
  }
  if (following.some((node) => !isBlank(node))) {
    const rest = list.cloneNode(false) as HTMLElement;
    following.forEach((node) => rest.appendChild(node));
    fragment.appendChild(rest);
  }

  const last = fragment.lastChild;
  list.after(fragment);

  if (isEmpty(item)) {
    item.remove();
  }
  if (!list.querySelector('li')) {
    list.remove();
  }

  return last;
};

const liftListStrays = (root: HTMLElement): void => {
  root.querySelectorAll('ul, ol').forEach((list) => {
    const children = Array.from(list.childNodes);
    if (children.every((child) => child.nodeName === 'LI' || isBlank(child))) {
      return;
    }

    const fragment = document.createDocumentFragment();
    let current: Element | null = null;

    children.forEach((child) => {
      if (child.nodeName === 'LI') {
        if (!current) {
          current = list.cloneNode(false) as Element;
          fragment.appendChild(current);
        }
        current.appendChild(child);
      } else if (!isBlank(child)) {
        current = null;
        fragment.appendChild(child);
      }
    });

    list.replaceWith(fragment);
  });
};

const normalizeLists = (root: HTMLElement): Node | null => {
  let last: Node | null = null;
  let block = root.querySelector(LIST_BLOCKS);

  while (block) {
    last = liftListBlock(block);
    block = root.querySelector(LIST_BLOCKS);
  }
  liftListStrays(root);

  return last;
};

const withFullWidthLine = (body: HTMLElement): HTMLElement => {
  if (body.lastElementChild?.hasAttribute('data-columns')) {
    body.insertAdjacentHTML('beforeend', '<p><br></p>');
  }

  return body;
};

const fillBlocks = (html: string, blocks?: Blocks): string => {
  const body = withFullWidthLine(parseBody(html));
  normalizeLists(body);

  if (blocks) {
    body.querySelectorAll('[data-var]').forEach((node) => {
      const key = node.getAttribute('data-var') as QuoteBlock;
      node.innerHTML = blocks[key] ?? key;
      node.setAttribute('contenteditable', 'false');
    });
  }

  return body.innerHTML;
};

const toDataAlign = (body: HTMLElement): void => {
  body.querySelectorAll<HTMLElement>('[style], [align]').forEach((node) => {
    const align = node.style.textAlign || node.getAttribute('align') || '';
    node.removeAttribute('style');
    node.removeAttribute('align');

    if (align === 'justify') {
      node.setAttribute('data-align', 'justify');
    } else if (align) {
      node.removeAttribute('data-align');
    }
  });
};

const emptyBlocks = (html: string): string => {
  const body = parseBody(html);
  normalizeLists(body);
  body.querySelectorAll('[data-var]').forEach((node) => {
    node.innerHTML = '';
    node.removeAttribute('contenteditable');
  });
  toDataAlign(body);

  return body.innerHTML;
};

interface Props {
  value: string;
  onChange: (html: string) => void;
  info?: QuoteTextInfo;
  missingBlocks?: QuoteBlock[];
  hint?: string;
}

export const QuoteRichTextEditor = ({
  value,
  onChange,
  info,
  missingBlocks,
  hint,
}: Props) => {
  const classes = useStyles();
  const { blocks, template } = info ?? {};
  const { t } = useTranslation();
  const [html, setHtml] = useState('');
  const wrapper = useRef<HTMLDivElement | null>(null);
  const filled = useRef(false);
  const written = useRef('');

  const blocksKey = blocks ? JSON.stringify(blocks) : '';

  useEffect(() => {
    if (filled.current || (template === undefined && !value)) {
      return;
    }
    filled.current = true;
    written.current = blocksKey;
    setHtml(fillBlocks(value || template || '', blocks));
  }, [template, value, blocks, blocksKey]);

  useEffect(() => {
    if (!filled.current || !blocks || blocksKey === written.current) {
      return;
    }
    written.current = blocksKey;
    setHtml((current) => fillBlocks(emptyBlocks(current), blocks));
  }, [blocks, blocksKey]);

  const change = useCallback(
    (event: { target: { value: string } }) => {
      setHtml(event.target.value);
      onChange(emptyBlocks(event.target.value));
    },
    [onChange],
  );

  const onEditorMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      const root = wrapper.current?.querySelector<HTMLElement>(
        '.rsw-ce[contenteditable]',
      );
      if (!root || event.target !== root) {
        return;
      }

      const written =
        root.lastElementChild?.hasAttribute('data-columns') ?? false;
      if (written) {
        root.insertAdjacentHTML('beforeend', '<p><br></p>');
      }

      const line = root.lastElementChild;
      if (!line) {
        return;
      }

      event.preventDefault();
      const range = document.createRange();
      range.selectNodeContents(line);
      range.collapse(false);

      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      root.focus();

      if (written) {
        root.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    [],
  );

  const onEditorPaste = useCallback(() => {
    setTimeout(() => {
      const root = wrapper.current?.querySelector<HTMLElement>(
        '.rsw-ce[contenteditable]',
      );
      const last = root && normalizeLists(root);
      if (!root || !last) {
        return;
      }

      const range = document.createRange();
      range.selectNodeContents(last);
      range.collapse(false);

      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      root.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }, []);

  const reset = useCallback(() => {
    if (template === undefined) {
      return;
    }
    written.current = blocksKey;
    setHtml(fillBlocks(template, blocks));
    onChange(template);
  }, [template, blocks, blocksKey, onChange]);

  const missing = missingBlocks ?? [];

  return (
    <>
      {(!!hint || template !== undefined) && (
        <div className={classes.head}>
          <span className={classes.hint}>{hint}</span>
          {template !== undefined && (
            <Button color="ink" variant="muted" small onClick={reset}>
              {t('quote.richText.reset')}
            </Button>
          )}
        </div>
      )}

      <div
        ref={wrapper}
        className={classes.editor}
        onMouseDown={onEditorMouseDown}
        onPaste={onEditorPaste}
        style={
          {
            '--quote-full-width-label': JSON.stringify(
              t('quote.header.fullWidth'),
            ),
          } as CSSProperties
        }
      >
        <Editor value={html} onChange={change}>
          <Toolbar>
            <BtnBold />
            <BtnItalic />
            <BtnStrikeThrough />
            <Separator />
            <BtnBulletList />
            <BtnJustify />
            <Separator />
            <BtnUndo />
            <BtnRedo />
          </Toolbar>
        </Editor>
      </div>

      {!!missing.length && (
        <span className={classes.missing}>
          <i className="ri-error-warning-line" />
          {t('quote.richText.missingBlocks', {
            blocks: missing
              .map((block) => t(`quote.blocks.${block}`))
              .join(', '),
          })}
        </span>
      )}
    </>
  );
};
